import { X } from "lucide-react";

export function Modal({ open, onClose, title, children, footer, testid, size = "md" }) {
  if (!open) return null;
  const widths = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-white rounded-lg shadow-2xl w-full ${widths[size]} max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h3 className="font-display font-bold text-lg text-asphalt">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-zinc-500 hover:text-asphalt"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function StatCard({ label, value, hint, accent = false, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className={`stat ${accent ? "bg-asphalt text-white border-asphalt" : ""}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-[11px] uppercase tracking-widest font-semibold ${accent ? "text-cab" : "text-zinc-500"}`}>{label}</div>
          <div className={`font-display font-bold text-3xl mt-2 ${accent ? "text-cab" : "text-asphalt"}`}>{value}</div>
          {hint && <div className={`text-xs mt-1 ${accent ? "text-zinc-400" : "text-zinc-500"}`}>{hint}</div>}
        </div>
        {Icon && <div className={`p-2 rounded-md ${accent ? "bg-cab/20" : "bg-zinc-100"}`}><Icon className={`h-4 w-4 ${accent ? "text-cab" : "text-asphalt"}`} /></div>}
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display font-bold text-3xl text-asphalt tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Field({ label, children, hint, required }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">{label}{required && " *"}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full px-3 py-2 rounded-md border border-zinc-300 focus:ring-2 focus:ring-cab focus:border-cab outline-none text-sm ${props.className || ""}`}
    />
  );
}

export function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full px-3 py-2 rounded-md border border-zinc-300 focus:ring-2 focus:ring-cab focus:border-cab outline-none text-sm bg-white ${props.className || ""}`}
    >
      {children}
    </select>
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full px-3 py-2 rounded-md border border-zinc-300 focus:ring-2 focus:ring-cab focus:border-cab outline-none text-sm ${props.className || ""}`}
    />
  );
}

export function Btn({ variant = "primary", as: Tag = "button", children, ...props }) {
  const variants = {
    primary: "bg-cab text-asphalt hover:brightness-105",
    dark: "bg-asphalt text-white hover:bg-zinc-800",
    outline: "border border-zinc-300 text-asphalt hover:bg-zinc-50",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "text-zinc-600 hover:bg-zinc-100",
  };
  return (
    <Tag
      {...props}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 ${variants[variant]} ${props.className || ""}`}
    >
      {children}
    </Tag>
  );
}

export function StatusPill({ status }) {
  const map = {
    active: "badge-success",
    available: "badge-success",
    completed: "badge-success",
    confirmed: "badge-info",
    allocated: "badge-info",
    running: "badge-warn",
    on_trip: "badge-warn",
    maintenance: "badge-warn",
    inquiry: "badge-muted",
    cancelled: "badge-error",
    suspended: "badge-error",
    trial: "badge-warn",
    paid: "badge-success",
    captured: "badge-success",
  };
  return <span className={`badge ${map[status] || "badge-muted"}`}>{status?.replace("_", " ")}</span>;
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="bg-white border border-dashed border-zinc-300 rounded-lg p-12 text-center">
      {Icon && <div className="inline-flex h-12 w-12 rounded-full bg-zinc-100 items-center justify-center mb-3"><Icon className="h-5 w-5 text-zinc-500" /></div>}
      <div className="font-display font-bold text-asphalt text-lg">{title}</div>
      {hint && <div className="text-sm text-zinc-500 mt-1">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
